/**
 * 等待室：人还不够（或房主还没开局）时，从牌桌**底部浮出来的一层抽屉**。
 *
 * ── 为什么是抽屉而不是居中卡片 ─────────────────────────────
 * 旧版拿一张 360px 的卡片盖在舞台正中，再压一层全屏遮罩：牌桌被糊掉，
 * 「我坐在这张桌子边上」的空间感全没了，开局的瞬间又像换了一个页面。
 * 现在只占屏幕下半部分，上半部分的毛毡、座位、自己的位置都看得见；
 * 容器本身 `pointer-events:none`，只有抽屉收事件，牌桌不会被这层挡住。
 *
 * ── 一个屏幕只有一个主 CTA（用户亲自指出的问题）────────────
 * 旧版同时摆着「回到牌局」和「继续牌局」两个同等重量的黄色按钮，语义还重叠。
 * 现在按当前状态**只显示该显示的那一个**：
 *
 *   自己输光了       → 「买分」；已经申请了 → 没有按钮，一行「等房主批准」
 *   自己被坐出了     → 「回到牌局」（不先回来，房主点什么都开不了局）
 *   还没入座（旁观） → 「选个座位坐下」
 *   房主，开不了局但有买分待批 → 「处理买分」（批了才开得了）
 *   是房主且能开    → 「开始游戏 / 继续牌局」
 *   其余            → 没有主按钮，只有一行「等待房主开始」
 *
 * 其余动作（邀请、加 AI）一律降级成描边按钮。
 *
 * ── 点不动必须说明为什么（P5）─────────────────────────────
 * 两个人都处于「坐出」状态时，房主点「继续牌局」服务端只会把 phase 置成 running，
 * 随即因为 `canStartHand()` 不过又挂回 paused —— 界面上毫无反应。
 * 现在 `startGate`/`seatReadiness` 把服务端那套判据原样搬了过来，
 * 按钮在点之前就置灰，并在**按钮正下方**写清原因与做法；
 * 另外任何被服务端拒绝的操作都会即时冒一条 Toast。
 */
import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { PlayerView, PublicSeat } from '@poker/shared'

import { IconPlus, IconShare, IconUser } from '../icons/index.js'
import { useUiStore } from '../store/index.js'
import { selectNetView, useNetStore } from '../transport/applyMessage.js'

import { errorText } from './errorText.js'
import { SeatList } from './SeatList.js'
import {
  canAddBot,
  emptySeatIndexes,
  groupRoomCode,
  heroSeatOf,
  hostTopUpGate,
  isHost,
  kickMessage,
  seatReadiness,
  seatedCount,
  startGate,
} from './roomDerive.js'
import { topUpOfSeat, waitingRoomOwnsTopUp } from './topUpDerive.js'
import { buildAddBotMessage } from './botPersona.js'
import { useRoomLink } from './roomLink.js'
import { useTalkStore } from '../talk/talkStore.js'
import './room.css'

export interface WaitingRoomProps {
  /** 覆盖 store 里的视图（回放 / storybook / 单测用）；省略就订阅 store。 */
  readonly view?: PlayerView | null
}

/**
 * 服务端拒绝 → Toast。
 *
 * 等待室里的每个按钮都可能被拒（不是房主、房间满了、AI 到上限…），
 * 而这些拒绝原本只落在 `netStore.lastError` 里，界面上一点反应都没有。
 */
function useRejectionToast(): void {
  const lastError = useNetStore((s) => s.lastError)
  const clearError = useNetStore((s) => s.clearError)
  const showToast = useUiStore((s) => s.showToast)

  useEffect(() => {
    if (lastError === null) return
    // 聊天发出去被拒的那几帧由抽屉自己认领（就地提示、把字还回去），这里不再弹一遍
    if (useTalkStore.getState().isTalkCid(lastError.refCid)) return
    showToast(errorText(lastError.code, lastError.message), 'warn')
    // 读完就清：同一个码第二次发生时要能再弹一次
    clearError()
  }, [lastError, clearError, showToast])
}

export function WaitingRoom({ view: viewProp }: WaitingRoomProps = {}): ReactNode {
  const storeView = useNetStore(selectNetView)
  const view = viewProp === undefined ? storeView : viewProp
  const link = useRoomLink()
  const openSheet = useUiStore((s) => s.openSheet)

  useRejectionToast()

  /** 移出：AI 发 removeBot（不拉黑），真人发 kickPlayer（拉黑，同一房间码回不来） */
  const kick = useCallback(
    (seat: PublicSeat): void => {
      const msg = kickMessage(seat)
      if (msg !== null) link.send(msg)
    },
    [link],
  )

  /**
   * 两个按钮共用一条路：整条消息由 `buildAddBotMessage` 算好，这里只负责发。
   *
   * - 补位 AI 每次挑一个**桌上还没有的**性格。以前写死 'balanced'，
   *   于是一桌加五个 AI，五个都是同一个打法、同一张脸 —— 而另外五种性格
   *   有名字、有头像、有完整策略，却没有任何入口能加进来。
   * - 大模型 AI 单独一个按钮，也因此**不在**补位的随机池里：
   *   每手牌都要花一次远程推理，混进去会让人在不知情的情况下烧 API 额度。
   *
   * 房主想指定具体哪一种，走房间设置；等待室不该变成一张参数表。
   */
  const addBotOf = useCallback(
    (kind: 'filler' | 'agent'): void => {
      const msg = buildAddBotMessage(useNetStore.getState().view, kind)
      if (msg !== null) link.send(msg)
    },
    [link],
  )
  /**
   * 抽屉收起与否。刻意放本地 state 而不是 store：它只影响这一页的这一屏，
   * 而且离开等待室（开局）之后整个组件就卸载了，没有「记住」的必要。
   */
  const [collapsed, setCollapsed] = useState(false)

  const addBot = useCallback((): void => {
    addBotOf('filler')
  }, [addBotOf])
  const addAgent = useCallback((): void => {
    addBotOf('agent')
  }, [addBotOf])

  /**
   * 「回到牌局」。连续 3 次超时托管会被服务端自动坐出（`noteAutoAction`），
   * 没有这个按钮的话人就再也回不来了 —— 房主按「继续牌局」也只会立刻又因
   * 「活人不足 2」暂停，两边都以为是对方卡住了。
   */
  const sitIn = useCallback((): void => {
    link.send({ type: 'setSitOutNextHand', sitOut: false })
  }, [link])

  const start = useCallback((): void => {
    // 暂停中要发 pauseGame{paused:false}：startGame 在 running/paused 下会回
    // E_GAME_ALREADY_RUNNING，按钮会变成「点了没反应」。
    const paused = (viewProp === undefined ? useNetStore.getState().view : viewProp)?.room.phase === 'paused'
    link.send(paused ? { type: 'pauseGame', paused: false } : { type: 'startGame' })
  }, [link, viewProp])

  if (view === null) return null

  const host = isHost(view)
  const hero = heroSeatOf(view)
  const seated = seatedCount(view)
  const maxSeats = view.room.config.maxSeats
  const free = emptySeatIndexes(view).length
  const gate = startGate(view)
  const ready = seatReadiness(view)
  const need = Math.max(2, view.room.config.minPlayersToStart)
  const autoStartWaiting = view.room.phase === 'lobby' && view.room.config.autoStart
  // 自己被坐出了吗（超时托管满 3 次 / 自己主动坐出）
  const heroSeat = hero === null ? null : (view.seats.find((s) => s.index === hero) ?? null)
  const heroSitOut = heroSeat !== null && (heroSeat.sitOutNextHand || heroSeat.presence === 'sittingOut')
  /**
   * ★ 坐出且筹码见底：服务端的 `sitIn` 在 `stack <= 0` 时**直接 return，一帧都不发**
   *   （presence.ts 里写着理由：放破产的人回桌只会在下个手牌边界被再踢回来）。
   *   所以「回到牌局」这颗按钮对他来说是死的 —— 点多少次都没反应、也没有任何提示。
   *   真正该做的事是买分，而它以前被这颗无效按钮从主位置挤了下去。
   * 与舞台买分胶囊让位是同一条判据（topUpDerive.waitingRoomOwnsTopUp）：这里给了「买分 / 等房主批准」，
   * 胶囊才不画；筹码没用完的人这里没有买分入口，胶囊照画。
   */
  const heroBroke = waitingRoomOwnsTopUp(view)
  /**
   * ★ 自己的买分已经交了（待批 / 已批待到账）：不再给金色「买分」，也不再催「先买分」——
   *   以前申请完这里照旧是那颗按钮和那句话，像是申请没交上去。进度只说一句状态，
   *   不做成按钮（舞台角上那颗买分胶囊被这个抽屉盖住了，这里替它说）。
   */
  const myTopUp = heroBroke ? topUpOfSeat(view, hero) : null
  const hostTopUp = hostTopUpGate(view)

  /**
   * 按钮下面那行字：说清「为什么」与「怎么办」。
   * 优先级 = 用户此刻最该做的那件事，绝不同时挂两条。
   */
  const hint = myTopUp !== null
    ? null
    : heroBroke
    ? '筹码用完了，先买分才能继续'
    : heroSitOut
    ? '你已暂离，不会发牌'
    : host
      ? gate.reason
      : ready.seated < need
        ? `还差 ${need - ready.seated} 人坐下`
        : ready.playable < need
          ? '有人暂离中，等他们回到牌局'
          : null

  return (
    <div className="waiting" role="region" aria-label="等待开局">
      {/* 只在抽屉上方压一道很浅的渐变：保证文字可读，但牌桌仍然看得见 */}
      <div className="waiting__fade" aria-hidden="true" />

      <div className="waiting__sheet" data-collapsed={collapsed ? 'true' : undefined}>
        {/**
          * ★ 这根抓手以前是纯装饰 div（36×4、aria-hidden、零事件）。
          *   可它长得就是「拖我收起」，用户当然会去点 —— 点了什么都不发生，
          *   于是报成「小窗收不起来，那个『-』表示可以操作但实际无法操作」。
          *   抽屉盖住半张牌桌，想收起是真需求，所以接上而不是把它画得更死板。
          * ★ 命中区靠按钮自身的 padding 撑到 44px（--hit-min），
          *   视觉上仍然只有那根 36×4 的横条。
          */}
        <button
          type="button"
          className="waiting__handle-btn"
          aria-expanded={!collapsed}
          aria-controls="waiting-body"
          aria-label={collapsed ? '展开等待面板' : '收起等待面板'}
          onClick={() => setCollapsed((c) => !c)}
        >
          <span className="waiting__handle" aria-hidden="true" />
        </button>

        <header className="waiting__head">
          <div className="waiting__id">
            <span className="waiting__cap">房间码</span>
            {/* 房间码是唯一不依赖链接的分发路径（DEVICES.md R3），等待室里必须常驻 */}
            <span className="waiting__code code selectable" aria-label={`房间码 ${view.room.code}`}>
              {groupRoomCode(view.room.code)}
            </span>
          </div>
          <span className="waiting__count num">
            <IconUser size={16} />
            {seated}/{maxSeats}
          </span>
        </header>

        <div className="waiting__body" id="waiting-body">
        <div className="waiting__seats">
          {/* ★ 名单只列**已入座**的人。空座不在这里逐行铺开有两个理由：
              一是 8 人房会铺出 6 行虚线占满整个抽屉，把牌桌彻底挡死；
              二是「挑哪个位置」是选座面板（SitDownSheet）该干的事，
              在这里再放一排「坐下」等于把同一件事做两遍，还和主 CTA 打架。 */}
          {/* ★ 不传手牌：等待室只在「没有手牌 / 上一手已经打完」时出现，那一手的
              「已弃牌 / ALL IN / 行动中」和 D / SB / BB 都是过期信息 —— 暂停后名单里还挂着
              「已弃牌」，读起来像自己这一手还没打完。下一手的位置由下一手说 */}
          <SeatList
            seats={view.seats}
            hand={null}
            heroSeat={hero}
            canKick={host}
            onKick={kick}
            hideEmpty
            topUps={view.topUps ?? []}
          />
          {/* 一行状态就够：「怎么把人叫来」由「邀请朋友」那颗按钮和按钮下方的原因行负责 */}
          {seated === 0 ? (
            <p className="waiting__free">还没有人入座</p>
          ) : (
            free > 0 && (
              <p className="waiting__free">
                还有 <span className="num">{free}</span> 个空位
              </p>
            )
          )}
        </div>

        <div className="waiting__actions">
          {/* ★ 主 CTA 只有一个：按当前状态选出那件最该做的事 */}
          {myTopUp !== null ? (
            <span className="waiting__wait" role="status">
              {myTopUp.status === 'approved' ? '本手后到账' : '等房主批准'}
            </span>
          ) : heroBroke ? (
            // 破产时主 CTA 换成买分：「回到牌局」在服务端是空操作，摆在这里只会骗人
            <button type="button" className="btn btn--cta pressable" onClick={() => openSheet('topUp')}>
              买分
            </button>
          ) : heroSitOut ? (
            <button type="button" className="btn btn--cta pressable" onClick={sitIn}>
              回到牌局
            </button>
          ) : hero === null ? (
            <button
              type="button"
              className="btn btn--cta pressable"
              onClick={() => openSheet('seatPicker')}
            >
              选个座位坐下
            </button>
          ) : hostTopUp ? (
            // 开不了局、又有人在等批买分：能让牌局继续的那一步就是去批
            <button
              type="button"
              className="btn btn--cta pressable"
              aria-describedby={hint === null ? undefined : 'waiting-hint'}
              onClick={() => openSheet('topUpQueue')}
            >
              处理买分
            </button>
          ) : host ? (
            <button
              type="button"
              className="btn btn--cta pressable"
              disabled={!gate.canStart}
              aria-describedby={hint === null ? undefined : 'waiting-hint'}
              onClick={start}
            >
              {gate.label}
            </button>
          ) : (
            <span className="waiting__wait">{autoStartWaiting ? '坐满自动开始，也可由房主提前开始' : '等待房主开始'}</span>
          )}

          {/* 次要动作：描边 / 文字按钮，明确低于主 CTA 一级。
              字一行排完不折行；图标 16px，窄到三颗放不下时 CSS 先把图标去掉（room.css） */}
          <div className="waiting__more">
            <button type="button" className="btn btn--quiet" onClick={() => openSheet('roomInfo')}>
              <IconShare size={16} />
              邀请朋友
            </button>
            {host && canAddBot(view) && (
              <>
                <button type="button" className="btn btn--quiet" onClick={addBot}>
                  <IconPlus size={16} />
                  加 AI 补位
                </button>
                <button type="button" className="btn btn--quiet" onClick={addAgent}>
                  <IconPlus size={16} />
                  加大模型 AI
                </button>
              </>
            )}
          </div>
        </div>

        {autoStartWaiting && host && (
          <p className="waiting__hint">坐满 {maxSeats} 人自动开始，也可提前开始</p>
        )}

        {/* 点不动的原因写在按钮正下方，而不是让用户点了没反应（P5） */}
        {hint !== null && (
          <p className="waiting__hint" id="waiting-hint" role="status">
            {hint}
          </p>
        )}
        </div>
      </div>
    </div>
  )
}

export default WaitingRoom
